import { useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import {
  buildAdditionalExpenseDirtyPayload,
  buildAdditionalExpensePayload,
  isAdditionalExpenseDirty,
  isAdditionalExpenseSaveDisabled,
  mapAdditionalExpenseToFormValues,
} from "../components/other-liens-balance/helpers/functions";
import { useSettlementStore } from "../store/settlementStore";

export default function useOtherLiensBalanceModal({
  caseId,
  mode = "edit",
  openModal,
  expenseData,
  handleCloseModal,
  onSaveSuccess,
}) {
  const additionalExpenseRepository = useService("repositories/additional-expense-repository");
  const { openFailureModal, openSuccessModal } = getModalHandler();
  const bumpRefreshVersion = useSettlementStore((state) => state.bumpRefreshVersion);
  const initialFormValues = useMemo(() => mapAdditionalExpenseToFormValues(expenseData), [expenseData]);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (openModal) {
      setFormValues(initialFormValues);
      setIsSaving(false);
    }
  }, [initialFormValues, openModal]);

  const isDirty = useMemo(
    () => isAdditionalExpenseDirty(initialFormValues, formValues),
    [formValues, initialFormValues]
  );
  const isSaveDisabled = useMemo(() => isAdditionalExpenseSaveDisabled(formValues), [formValues]);

  const handleFieldOnChange = (fieldName, value) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCancel = () => {
    setFormValues(initialFormValues);
  };

  const handleSave = async () => {
    if (!isDirty || isSaveDisabled) {
      return;
    }

    try {
      setIsSaving(true);

      if (mode === "add") {
        await additionalExpenseRepository.post(buildAdditionalExpensePayload(formValues, caseId));
      } else if (expenseData?.pk || expenseData?.id) {
        const expenseId = expenseData?.pk || expenseData?.id;
        await additionalExpenseRepository.patch(
          expenseId,
          buildAdditionalExpenseDirtyPayload(initialFormValues, formValues)
        );
      } else {
        return;
      }

      await onSaveSuccess?.();
      bumpRefreshVersion();
      openSuccessModal();
      handleCloseModal();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formValues,
    isDirty,
    isSaving,
    isSaveDisabled,
    handleFieldOnChange,
    handleCancel,
    handleSave,
  };
}
